import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { Download, TrendingUp, TrendingDown, AlertCircle, IndianRupee, Home, Calendar, Target } from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: 'pnl', label: 'P&L Statement', icon: IndianRupee },
  { id: 'collection', label: 'Collection %', icon: Target },
  { id: 'aging', label: 'Aging Report', icon: AlertCircle },
  { id: 'occupancy', label: 'Occupancy', icon: Home },
  { id: 'trend', label: 'Monthly Trend', icon: TrendingUp },
];

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const Reports = () => {
  const { token } = useAuth();
  const [tab, setTab] = useState('pnl');
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const [range, setRange] = useState({ start: firstOfMonth, end: today });

  const [pnl, setPnl] = useState(null);
  const [collection, setCollection] = useState(null);
  const [aging, setAging] = useState(null);
  const [occupancy, setOccupancy] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const h = { headers: { Authorization: `Bearer ${token}` } };
      const qs = `start_date=${range.start}&end_date=${range.end}`;
      const [pnlR, colR, ageR, occR, trR] = await Promise.all([
        axios.get(`${API}/reports/pnl?${qs}`, h),
        axios.get(`${API}/reports/collection?${qs}`, h),
        axios.get(`${API}/reports/aging`, h),
        axios.get(`${API}/reports/occupancy`, h),
        axios.get(`${API}/reports/monthly-trend?months=6`, h),
      ]);
      setPnl(pnlR.data);
      setCollection(colR.data);
      setAging(ageR.data);
      setOccupancy(occR.data);
      setTrend(trR.data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [token, range]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const exportSheet = (rows, name) => {
    if (!rows?.length) return;
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, name);
    XLSX.writeFile(wb, `${name}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="reports-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-600 mt-1">Financial insights, aging & occupancy</p>
        </div>
        <div className="flex items-center gap-3">
          <div>
            <Label className="text-xs">From</Label>
            <Input data-testid="report-start" type="date" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })} className="w-40" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input data-testid="report-end" type="date" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })} className="w-40" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap border-b border-gray-200">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`report-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-all ${
                tab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>
      ) : (
        <>
          {tab === 'pnl' && pnl && (
            <div className="space-y-6" data-testid="pnl-content">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-5 bg-gradient-to-br from-green-500 to-green-600 text-white">
                  <p className="text-green-100 text-xs">Total Income</p>
                  <p className="text-3xl font-bold mt-2">₹{pnl.total_income.toLocaleString()}</p>
                  <p className="text-green-100 text-xs mt-1">{pnl.payments_count} txns</p>
                </Card>
                <Card className="p-5 bg-gradient-to-br from-red-500 to-red-600 text-white">
                  <p className="text-red-100 text-xs">Total Expenses</p>
                  <p className="text-3xl font-bold mt-2">₹{pnl.total_expenses.toLocaleString()}</p>
                  <p className="text-red-100 text-xs mt-1">{pnl.expenses_count} txns</p>
                </Card>
                <Card className={`p-5 bg-gradient-to-br text-white ${pnl.net_profit >= 0 ? 'from-blue-500 to-blue-700' : 'from-orange-500 to-red-600'}`}>
                  <p className="text-white/80 text-xs">Net Profit</p>
                  <p className="text-3xl font-bold mt-2">₹{pnl.net_profit.toLocaleString()}</p>
                  <p className="text-white/80 text-xs mt-1">{pnl.net_profit >= 0 ? <TrendingUp className="inline" size={14}/> : <TrendingDown className="inline" size={14}/>} {pnl.margin_pct}% margin</p>
                </Card>
                <Card className="p-5 border-l-4 border-l-purple-500">
                  <p className="text-xs text-gray-500">Period</p>
                  <p className="text-sm font-bold text-gray-900 mt-2">{pnl.period.start_date}</p>
                  <p className="text-sm font-bold text-gray-900">to {pnl.period.end_date}</p>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Income by Type</h3>
                  {Object.keys(pnl.income_by_type).length === 0 ? (
                    <p className="text-gray-500 text-sm py-12 text-center">No income in this period</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={Object.entries(pnl.income_by_type).map(([k, v]) => ({ name: k, value: v }))}
                          cx="50%" cy="50%" outerRadius={90} dataKey="value" label
                        >
                          {Object.keys(pnl.income_by_type).map((name, i) => <Cell key={name} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={v => `₹${v.toLocaleString()}`} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Card>
                <Card className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Expenses by Category</h3>
                  {Object.keys(pnl.expense_by_category).length === 0 ? (
                    <p className="text-gray-500 text-sm py-12 text-center">No expenses in this period</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={Object.entries(pnl.expense_by_category).map(([k, v]) => ({ name: k, value: v }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip formatter={v => `₹${v.toLocaleString()}`} />
                        <Bar dataKey="value" fill="#ef4444" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </div>

              <Button
                data-testid="pnl-export"
                onClick={() => exportSheet([
                  { section: 'Summary', income: pnl.total_income, expenses: pnl.total_expenses, profit: pnl.net_profit },
                  ...Object.entries(pnl.income_by_type).map(([k, v]) => ({ section: 'Income', category: k, amount: v })),
                  ...Object.entries(pnl.expense_by_category).map(([k, v]) => ({ section: 'Expenses', category: k, amount: v })),
                ], 'PnL_Report')}
                className="bg-green-600 hover:bg-green-700"
              >
                <Download size={16} className="mr-2" /> Export P&L to Excel
              </Button>
            </div>
          )}

          {tab === 'collection' && collection && (
            <div className="space-y-6" data-testid="collection-content">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-5 bg-gradient-to-br from-blue-500 to-blue-700 text-white">
                  <p className="text-blue-100 text-xs">Expected</p>
                  <p className="text-3xl font-bold mt-2">₹{collection.expected_total.toLocaleString()}</p>
                </Card>
                <Card className="p-5 bg-gradient-to-br from-green-500 to-green-700 text-white">
                  <p className="text-green-100 text-xs">Collected</p>
                  <p className="text-3xl font-bold mt-2">₹{collection.collected_total.toLocaleString()}</p>
                </Card>
                <Card className="p-5 bg-gradient-to-br from-red-500 to-red-700 text-white">
                  <p className="text-red-100 text-xs">Pending</p>
                  <p className="text-3xl font-bold mt-2">₹{collection.pending_total.toLocaleString()}</p>
                </Card>
                <Card className="p-5 bg-gradient-to-br from-purple-500 to-purple-700 text-white">
                  <p className="text-purple-100 text-xs">Collection %</p>
                  <p className="text-3xl font-bold mt-2">{collection.collection_pct}%</p>
                </Card>
              </div>
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Tenant-wise Collection</h3>
                  <Button data-testid="collection-export" size="sm" onClick={() => exportSheet(collection.tenant_rows, 'Collection_Report')} className="bg-green-600 hover:bg-green-700">
                    <Download size={14} className="mr-1" /> Export
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 font-semibold">Tenant</th>
                        <th className="text-left p-3 font-semibold">Phone</th>
                        <th className="text-right p-3 font-semibold">Expected</th>
                        <th className="text-right p-3 font-semibold">Collected</th>
                        <th className="text-right p-3 font-semibold">Balance</th>
                        <th className="text-right p-3 font-semibold">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {collection.tenant_rows.length === 0 ? (
                        <tr><td colSpan="6" className="text-center p-8 text-gray-500">No active tenants</td></tr>
                      ) : collection.tenant_rows.map(r => (
                        <tr key={r.tenant_id} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium">{r.name}</td>
                          <td className="p-3 text-gray-600">{r.phone}</td>
                          <td className="p-3 text-right">₹{r.expected.toLocaleString()}</td>
                          <td className="p-3 text-right text-green-600 font-semibold">₹{r.collected.toLocaleString()}</td>
                          <td className={`p-3 text-right font-semibold ${r.balance > 0 ? 'text-red-600' : 'text-gray-600'}`}>₹{r.balance.toLocaleString()}</td>
                          <td className="p-3 text-right">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${r.pct >= 100 ? 'bg-green-100 text-green-800' : r.pct >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                              {r.pct}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}

          {tab === 'aging' && aging && (
            <div className="space-y-6" data-testid="aging-content">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {Object.entries(aging.buckets).map(([bucket, amt], i) => (
                  <Card key={bucket} className={`p-5 border-l-4 ${['border-l-green-500', 'border-l-yellow-500', 'border-l-orange-500', 'border-l-red-600'][i]}`}>
                    <p className="text-xs text-gray-500">{bucket} days</p>
                    <p className="text-2xl font-bold mt-1">₹{amt.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">{aging.bucket_counts[bucket]} tenant(s)</p>
                  </Card>
                ))}
              </div>
              <Card className="p-6">
                <h3 className="font-semibold mb-4">Aging Distribution</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={Object.entries(aging.buckets).map(([k, v]) => ({ name: k, value: v }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={v => `₹${v.toLocaleString()}`} />
                    <Bar dataKey="value" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Overdue Tenants ({aging.total_tenants})</h3>
                  <Button data-testid="aging-export" size="sm" onClick={() => exportSheet(aging.rows, 'Aging_Report')} className="bg-green-600 hover:bg-green-700">
                    <Download size={14} className="mr-1" /> Export
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3">Name</th>
                        <th className="text-left p-3">Phone</th>
                        <th className="text-left p-3">Due Date</th>
                        <th className="text-right p-3">Days Overdue</th>
                        <th className="text-right p-3">Amount</th>
                        <th className="text-center p-3">Bucket</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aging.rows.length === 0 ? (
                        <tr><td colSpan="6" className="text-center p-8 text-gray-500">No overdue tenants 🎉</td></tr>
                      ) : aging.rows.map(r => (
                        <tr key={r.tenant_id} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium">{r.name}</td>
                          <td className="p-3 text-gray-600">{r.phone}</td>
                          <td className="p-3 text-gray-600">{r.due_date || '-'}</td>
                          <td className={`p-3 text-right font-bold ${r.days_overdue > 60 ? 'text-red-600' : r.days_overdue > 30 ? 'text-orange-600' : 'text-yellow-600'}`}>{r.days_overdue}</td>
                          <td className="p-3 text-right font-semibold">₹{r.amount.toLocaleString()}</td>
                          <td className="p-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              r.bucket === '0-30' ? 'bg-green-100 text-green-800' :
                              r.bucket === '31-60' ? 'bg-yellow-100 text-yellow-800' :
                              r.bucket === '61-90' ? 'bg-orange-100 text-orange-800' :
                              'bg-red-100 text-red-800'
                            }`}>{r.bucket}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}

          {tab === 'occupancy' && occupancy && (
            <div className="space-y-6" data-testid="occupancy-content">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-5 bg-gradient-to-br from-blue-500 to-blue-700 text-white">
                  <p className="text-blue-100 text-xs">Total Beds</p>
                  <p className="text-3xl font-bold mt-2">{occupancy.summary.total_beds}</p>
                </Card>
                <Card className="p-5 bg-gradient-to-br from-green-500 to-green-700 text-white">
                  <p className="text-green-100 text-xs">Occupied</p>
                  <p className="text-3xl font-bold mt-2">{occupancy.summary.occupied_beds}</p>
                </Card>
                <Card className="p-5 bg-gradient-to-br from-orange-500 to-orange-700 text-white">
                  <p className="text-orange-100 text-xs">Vacant</p>
                  <p className="text-3xl font-bold mt-2">{occupancy.summary.vacant_beds}</p>
                </Card>
                <Card className="p-5 bg-gradient-to-br from-purple-500 to-purple-700 text-white">
                  <p className="text-purple-100 text-xs">Occupancy %</p>
                  <p className="text-3xl font-bold mt-2">{occupancy.summary.occupancy_pct}%</p>
                </Card>
              </div>
              <Card className="p-6">
                <h3 className="font-semibold mb-4">Per-Property Occupancy</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={occupancy.properties}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="property_name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="occupied_beds" stackId="a" fill="#10b981" name="Occupied" />
                    <Bar dataKey="vacant_beds" stackId="a" fill="#f59e0b" name="Vacant" />
                  </BarChart>
                </ResponsiveContainer>
                <Button data-testid="occupancy-export" size="sm" onClick={() => exportSheet(occupancy.properties, 'Occupancy_Report')} className="mt-4 bg-green-600 hover:bg-green-700">
                  <Download size={14} className="mr-1" /> Export
                </Button>
              </Card>
            </div>
          )}

          {tab === 'trend' && trend && (
            <div className="space-y-6" data-testid="trend-content">
              <Card className="p-6">
                <h3 className="font-semibold mb-4">6-Month Income vs Expenses</h3>
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart data={trend.trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={v => `₹${v.toLocaleString()}`} />
                    <Legend />
                    <Area type="monotone" dataKey="income" stroke="#10b981" fill="#10b981" fillOpacity={0.35} />
                    <Area type="monotone" dataKey="expense" stroke="#ef4444" fill="#ef4444" fillOpacity={0.35} />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>
              <Card className="p-6">
                <h3 className="font-semibold mb-4">Profit Trend</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={trend.trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={v => `₹${v.toLocaleString()}`} />
                    <Line type="monotone" dataKey="profit" stroke="#3b82f6" strokeWidth={3} dot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
                <Button data-testid="trend-export" size="sm" onClick={() => exportSheet(trend.trend, 'Monthly_Trend')} className="mt-4 bg-green-600 hover:bg-green-700">
                  <Download size={14} className="mr-1" /> Export
                </Button>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
